<template>
  <div class="monitor-page" v-loading="detailLoading">
    <template v-if="!form">
      <div class="monitor-heading">
        <h2>监控策略</h2>
        <el-button v-if="can('import')" type="primary" size="small" icon="el-icon-plus" @click="openImport">引入共享策略</el-button>
      </div>
      <el-form :inline="true" size="small" class="monitor-query" @submit.native.prevent="search">
        <el-form-item label="策略名称"><el-input v-model.trim="filters.name" clearable placeholder="策略名称" @keyup.enter.native="search" /></el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filters.enabled" clearable placeholder="全部"><el-option label="启用" :value="true" /><el-option label="禁用" :value="false" /></el-select>
        </el-form-item>
        <el-form-item><el-button type="primary" icon="el-icon-search" @click="search">查询</el-button><el-button @click="reset">重置</el-button></el-form-item>
      </el-form>
      <el-alert v-if="listError" :title="listError" type="error" :closable="false" show-icon class="monitor-error" />
      <el-table :data="rows" v-loading="loading" size="small" class="monitor-table" :empty-text="listError ? '加载失败' : '暂无监控策略'">
        <el-table-column prop="id" label="ID" min-width="95" />
        <el-table-column prop="name" label="策略名称" min-width="190" show-overflow-tooltip />
        <el-table-column prop="priority" label="优先级" width="90" />
        <el-table-column label="来源共享策略" min-width="210">
          <template slot-scope="scope">
            <template v-if="hasSource(scope.row)">
              <div>{{ scope.row.sourceStrategyName }} <span class="muted">#{{ scope.row.sourceStrategyId }}</span></div>
              <span v-if="scope.row.sourceEnabled === false" class="warning-text">来源禁用或不可用</span>
            </template>
            <span v-else class="muted">SaaS独立配置</span>
          </template>
        </el-table-column>
        <el-table-column label="规则 / 等级" min-width="170">
          <template slot-scope="scope">
            <div>{{ ruleName(scope.row) }}</div>
            <el-button type="text" size="mini" @click="showDetail(scope.row.id)">查看 {{ scope.row.config.levels.length }} 个等级与措施</el-button>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80"><template slot-scope="scope"><el-tag size="small" :type="scope.row.enabled ? 'success' : 'info'">{{ scope.row.enabled ? '启用' : '禁用' }}</el-tag></template></el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip />
        <el-table-column prop="updater" label="修改人" min-width="110" show-overflow-tooltip />
        <el-table-column label="修改时间" width="165"><template slot-scope="scope">{{ timeText(scope.row.updatedTime) }}</template></el-table-column>
        <el-table-column label="操作" fixed="right" width="175">
          <template slot-scope="scope">
            <el-button v-if="can('edit')" type="text" size="mini" :disabled="!!busyId" @click="edit(scope.row.id)">编辑</el-button>
            <el-tooltip v-if="can('status')" :disabled="!enableBlocked(scope.row)" content="关联共享策略禁用或不可用，不能启用" placement="top">
              <span><el-button type="text" size="mini" :disabled="!!busyId || enableBlocked(scope.row)" @click="toggle(scope.row)">{{ scope.row.enabled ? '禁用' : '启用' }}</el-button></span>
            </el-tooltip>
            <el-button v-if="can('delete')" type="text" size="mini" class="danger-text" :disabled="!!busyId" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="monitor-pagination"><Pagination :page="page" :size="size" :total="total" @pageChanged="pageChanged" /></div>
    </template>

    <template v-else>
      <div class="monitor-heading">
        <h2>{{ form.id ? '编辑监控策略' : '引入共享策略' }}</h2>
        <div><el-button size="small" :disabled="saving" icon="el-icon-back" @click="leaveEditor">返回列表</el-button><el-button size="small" type="primary" :loading="saving" icon="el-icon-check" @click="save">保存</el-button></div>
      </div>
      <el-alert v-if="saveError" :title="saveError" type="error" :closable="false" show-icon class="monitor-error" />
      <el-form label-position="top" size="small" class="monitor-editor-meta" :disabled="saving">
        <div class="monitor-meta-line">
          <el-form-item label="策略名称" required><el-input v-model="form.name" maxlength="100" label="策略名称" /></el-form-item>
          <el-form-item label="优先级" required><el-input-number v-model="form.priority" :min="1" :max="2147483647" :precision="0" controls-position="right" /></el-form-item>
          <el-form-item label="状态"><el-tag :type="form.enabled ? 'success' : 'info'">{{ form.enabled ? '启用' : '禁用' }}</el-tag></el-form-item>
          <el-form-item label="来源共享策略"><span>{{ sourceLabel }}</span></el-form-item>
        </div>
        <el-form-item label="备注"><el-input v-model="form.remark" type="textarea" :rows="3" maxlength="500" label="备注" show-word-limit /></el-form-item>
      </el-form>
      <fieldset :disabled="saving" class="monitor-level-fieldset">
        <StrategyLevels :key="editorKey" :config="form.config" />
      </fieldset>
    </template>

    <el-dialog title="引入共享策略" :visible.sync="importVisible" width="min(1000px, 94vw)" :close-on-click-modal="false" :destroy-on-close="true">
      <el-form :inline="true" size="small" @submit.native.prevent="searchSources">
        <el-form-item><el-input v-model.trim="sourceName" clearable placeholder="共享策略名称" @keyup.enter.native="searchSources" /></el-form-item>
        <el-form-item><el-button type="primary" icon="el-icon-search" @click="searchSources">查询</el-button></el-form-item>
      </el-form>
      <el-alert v-if="sourceError" :title="sourceError" type="error" :closable="false" show-icon />
      <el-table :data="sources" v-loading="sourceLoading" size="small" highlight-current-row empty-text="没有可引入的已启用共享策略" @current-change="selectSource">
        <el-table-column label="选择" width="64"><template slot-scope="scope"><el-radio v-model="selectedSource" :label="scope.row.id"><span class="sr-only">{{ scope.row.name }}</span></el-radio></template></el-table-column>
        <el-table-column prop="id" label="ID" width="90" />
        <el-table-column prop="name" label="共享策略名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="priority" label="优先级" width="80" />
        <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
        <el-table-column label="组合与措施" width="120"><template slot-scope="scope"><el-button type="text" @click.stop="showDetail(scope.row.id, true)">查看完整组合</el-button></template></el-table-column>
      </el-table>
      <div class="monitor-pagination"><Pagination :page="sourcePage" :size="sourceSize" :total="sourceTotal" @pageChanged="sourcePageChanged" /></div>
      <div slot="footer"><el-button size="small" @click="importVisible = false">取消</el-button><el-button size="small" type="primary" :disabled="!selectedSource || sourceLoading" :loading="detailLoading" @click="configureSource">配置策略</el-button></div>
    </el-dialog>

    <el-dialog :title="detail ? detail.name + ' · 等级与措施' : '等级与措施'" :visible.sync="detailVisible" width="94%" append-to-body :destroy-on-close="true">
      <template v-if="detail">
        <div class="monitor-detail-meta"><span>ID：{{ detail.id }}</span><span>优先级：{{ detail.priority }}</span><el-tag size="small" :type="detail.enabled ? 'success' : 'info'">{{ detail.enabled ? '启用' : '禁用' }}</el-tag></div>
        <p v-if="detail.remark" class="monitor-remark">备注：{{ detail.remark }}</p>
        <StrategyLevels :key="detailKey" :config="detail.config" readonly />
      </template>
      <div slot="footer"><el-button size="small" @click="detailVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from 'components/pagination'
import StrategyLevels from './StrategyLevels.vue'
import api from './api'
import { clone, errorText, payload, timeText, validate } from './model'

export default {
  name: 'TenantMonitorStrategy',
  components: { Pagination, StrategyLevels },
  data() {
    return {
      filters: { name: '', enabled: '' }, rows: [], page: 1, size: 20, total: 0, loading: false, listError: '', listRequest: 0,
      importVisible: false, sources: [], sourceName: '', sourcePage: 1, sourceSize: 20, sourceTotal: 0, sourceLoading: false, sourceError: '', sourceRequest: 0, selectedSource: '',
      form: null, initial: '', sourceId: null, sourceLabel: '', editorKey: 0, saving: false, saveError: '', busyId: '',
      detail: null, detailVisible: false, detailKey: 0, detailLoading: false,
    }
  },
  created() { this.load() },
  mounted() { window.addEventListener('beforeunload', this.beforeUnload) },
  beforeDestroy() { window.removeEventListener('beforeunload', this.beforeUnload) },
  async beforeRouteLeave(to, from, next) { next(await this.confirmLeave()) },
  computed: {
    dirty() { return !!this.form && JSON.stringify(payload(this.form)) !== this.initial },
  },
  methods: {
    timeText,
    can(action) { return this.$hasPerm(`tenant:monitor_strategy:${action}`) },
    hasSource(row) { return row.sourceStrategyId && String(row.sourceStrategyId) !== '0' },
    enableBlocked(row) { return !row.enabled && this.hasSource(row) && row.sourceEnabled !== true },
    ruleName(row) { return row.config.snapshot.root.ruleName },
    async load() {
      const request = ++this.listRequest
      this.loading = true
      this.listError = ''
      try {
        const query = { page: this.page, size: this.size, name: this.filters.name }
        if (typeof this.filters.enabled === 'boolean') query.enabled = this.filters.enabled
        const result = await api.list(query)
        if (request !== this.listRequest) return
        this.rows = result.items || []
        this.total = Number(result.total || 0)
        if (!this.rows.length && this.total && this.page > 1) { this.page--; return this.load() }
      } catch (error) {
        if (request === this.listRequest) { this.listError = errorText(error); this.rows = []; this.total = 0 }
      } finally { if (request === this.listRequest) this.loading = false }
    },
    search() { this.page = 1; this.load() },
    reset() { this.filters = { name: '', enabled: '' }; this.search() },
    pageChanged({ page, size }) { this.page = page; this.size = size; this.load() },
    openImport() {
      this.sourceName = ''; this.sourcePage = 1; this.selectedSource = ''; this.importVisible = true
      this.loadSources()
    },
    async loadSources() {
      const request = ++this.sourceRequest
      this.sourceLoading = true; this.sourceError = ''; this.selectedSource = ''
      try {
        const result = await api.sharedOptions({ page: this.sourcePage, size: this.sourceSize, name: this.sourceName })
        if (request === this.sourceRequest) { this.sources = result.items || []; this.sourceTotal = Number(result.total || 0) }
      } catch (error) {
        if (request === this.sourceRequest) { this.sources = []; this.sourceTotal = 0; this.sourceError = errorText(error) }
      } finally { if (request === this.sourceRequest) this.sourceLoading = false }
    },
    searchSources() { this.sourcePage = 1; this.loadSources() },
    sourcePageChanged({ page, size }) { this.sourcePage = page; this.sourceSize = size; this.loadSources() },
    selectSource(row) { if (row) this.selectedSource = row.id },
    async configureSource() {
      if (!this.selectedSource || this.detailLoading) return
      this.detailLoading = true
      try {
        const source = await api.sharedOne(this.selectedSource)
        this.sourceId = source.id
        this.sourceLabel = `${source.name} #${source.id}`
        const form = clone(source)
        delete form.id
        form.enabled = false
        this.setForm(form)
        this.importVisible = false
      } catch (error) { this.sourceError = errorText(error) }
      finally { this.detailLoading = false }
    },
    setForm(form) {
      this.form = form; this.initial = JSON.stringify(payload(form)); this.saveError = ''; this.editorKey++
    },
    async edit(id) {
      this.detailLoading = true
      try {
        const form = await api.one(id)
        this.sourceId = null
        this.sourceLabel = this.hasSource(form) ? `${form.sourceStrategyName} #${form.sourceStrategyId}` : 'SaaS独立配置'
        this.setForm(clone(form))
      } catch (error) { this.$message.error(errorText(error)) }
      finally { this.detailLoading = false }
    },
    async showDetail(id, shared = false) {
      if (this.detailLoading) return
      this.detailLoading = true
      try {
        this.detail = await (shared ? api.sharedOne(id) : api.one(id))
        this.detailKey++; this.detailVisible = true
      } catch (error) { this.$message.error(errorText(error)) }
      finally { this.detailLoading = false }
    },
    async save() {
      if (this.saving) return
      this.saveError = validate(this.form)
      if (this.saveError) { this.$message.error(this.saveError); return }
      this.saving = true
      try {
        const data = payload(this.form)
        if (this.form.id) await api.save(data)
        else await api.importShared({ sharedStrategyId: this.sourceId, strategy: data })
        const imported = !this.form.id
        this.$message.success(imported ? '引入成功，策略为禁用状态' : '保存成功')
        this.form = null; this.initial = ''
        if (imported) { this.page = 1; this.filters = { name: '', enabled: '' } }
        await this.load()
      } catch (error) { this.saveError = errorText(error) }
      finally { this.saving = false }
    },
    async toggle(row) {
      if (this.busyId) return
      const enabled = !row.enabled
      try { await this.$confirm(`${enabled ? '启用' : '禁用'}策略“${row.name}”？`, '确认状态', { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }) }
      catch (_) { return }
      this.busyId = row.id
      try {
        await api.status({ id: row.id, enabled })
        this.$message.success(enabled ? '已启用' : '已禁用')
      } catch (error) { this.$message.error(errorText(error)) }
      finally { this.busyId = ''; await this.load() }
    },
    async remove(row) {
      if (this.busyId) return
      try { await this.$confirm(`删除策略“${row.name}”？删除后可以重新引入，原自定义参数不会恢复。`, '删除监控策略', { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }) }
      catch (_) { return }
      this.busyId = row.id
      try { await api.remove(row.id); this.$message.success('删除成功'); await this.load() }
      catch (error) { this.$message.error(errorText(error)) }
      finally { this.busyId = '' }
    },
    beforeUnload(event) { if (this.dirty) { event.preventDefault(); event.returnValue = '' } },
    async confirmLeave() {
      if (this.saving) return false
      if (!this.dirty) return true
      try { await this.$confirm('放弃尚未保存的修改？', '离开编辑', { confirmButtonText: '放弃修改', cancelButtonText: '继续编辑', type: 'warning' }); return true }
      catch (_) { return false }
    },
    async leaveEditor() { if (await this.confirmLeave()) { this.form = null; this.initial = '' } },
  },
}
</script>

<style lang="less" scoped>
.monitor-page { margin: 16px; padding: 20px 24px; background: #fff; color: #303133; min-width: 0; font-size: 14px; }
.monitor-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.monitor-heading h2 { margin: 0; font-size: 18px; line-height: 28px; }
.monitor-query { border-bottom: 1px solid #ebeef5; margin-bottom: 16px; }
.monitor-query .el-select { width: 130px; }
.monitor-error { margin-bottom: 16px; }
.monitor-pagination { display: flex; justify-content: center; padding-top: 20px; overflow-x: auto; }
.monitor-meta-line { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; }
.monitor-meta-line > .el-form-item:first-child { flex: 1 1 240px; max-width: 400px; }
.monitor-meta-line .el-input-number { width: 150px; }
.monitor-editor-meta { border-bottom: 1px solid #ebeef5; }
.monitor-level-fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
.monitor-detail-meta { display: flex; flex-wrap: wrap; gap: 24px; align-items: center; }
.monitor-remark { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 22px; }
.muted { color: #909399; }
.warning-text { color: #a86812; font-size: 12px; }
.danger-text { color: #d94b4b; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
@media (max-width: 760px) {
  .monitor-page { margin: 8px; padding: 14px; }
  .monitor-heading { align-items: flex-start; flex-wrap: wrap; }
  .monitor-heading > div { display: flex; gap: 8px; }
}
</style>
